/**
 * Strip common PII before text is sent to the Cloud Run / Vertex API.
 * Keep in sync with cloud-run/src/anonymizeForAi.js (same rules).
 */

import { getSettings } from './storage';

const escapeRegex = (s) => String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Do not treat placeholders or generic words as PII to redact (avoids corrupting prompts). */
export const isCoachablePersonalNameHint = (raw) => {
  const s = String(raw || '').trim();
  if (s.length < 2 || s.length > 80) return false;
  const lower = s.toLowerCase();
  if (['friend', 'user', 'name', '[user]', '[name]', 'null', 'undefined'].includes(lower)) return false;
  if (s.startsWith('[')) return false;
  return true;
};

const normalizeHints = (extraNames) => {
  const out = [];
  const seen = new Set();
  for (const raw of extraNames || []) {
    const n = String(raw || '').trim();
    if (n.length < 2 || n.length > 80) continue;
    const key = n.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(n);
  }
  return out.sort((a, b) => b.length - a.length);
};

const PHONE_LIKE =
  /\b(?:\+?\d{1,3}[\s.-]?)?(?:\(\d{2,4}\)|\d{2,4})[\s.-]?\d{2,4}[\s.-]?\d{2,4}[\s.-]?\d{2,9}\b/g;

/** Email/phone/url/postcode/address only — keep personal names intact (align with cloud-run). */
export function anonymizePiiOnly(text) {
  let t = String(text || '');

  /** Hyphenated calendar dates match PHONE_LIKE (e.g. 2026-05-01) — mask dates first. */
  const dateVault = [];
  const stashDate = (m) => {
    const i = dateVault.length;
    dateVault.push(m);
    return `__RBM_DATE_${i}__`;
  };
  t = t.replace(/\b\d{4}-\d{2}-\d{2}\b/g, stashDate);
  t = t.replace(/\b\d{1,2}-\d{1,2}-\d{4}\b/g, stashDate);

  t = t.replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[email]');

  t = t.replace(PHONE_LIKE, '[phone]');

  t = t.replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, '[ip]');

  t = t.replace(/\bhttps?:\/\/[^\s]+/gi, '[url]');

  t = t.replace(/\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/gi, '[postcode]');

  t = t.replace(
    /\b\d{1,5}\s+[A-Za-z0-9.'\s-]{2,40}\s+(?:Street|St\.?|Road|Rd\.?|Ave\.?|Avenue|Dr\.?|Drive|Lane|Ln\.?|Way|Boulevard|Blvd\.?|Court|Ct\.?|Place|Pl\.?)\b/gi,
    '[address]',
  );

  t = t.replace(/__RBM_DATE_(\d+)__/g, (_, i) => dateVault[Number(i)] ?? '');
  return t.replace(/\s+/g, ' ').trim();
}

export function anonymizeForAi(text, opts = {}) {
  let t = anonymizePiiOnly(text);
  const extraNames = normalizeHints(opts.extraNames);

  for (const name of extraNames) {
    const re = new RegExp(`\\b${escapeRegex(name)}\\b`, 'gi');
    t = t.replace(re, '[name]');
  }

  return t.replace(/\s+/g, ' ').trim();
}

/** Display name and any extra strings the client knows should be redacted from model prompts. */
export function collectClientAnonymizeHints(extra = []) {
  const names = [...(Array.isArray(extra) ? extra : [])];
  const s = getSettings()?.name?.trim();
  if (isCoachablePersonalNameHint(s)) {
    names.push(s);
    const first = s.split(/\s+/)[0];
    if (first && first !== s && isCoachablePersonalNameHint(first)) {
      names.push(first);
    }
  }
  return normalizeHints(names);
}
